import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DotCMSConfigurationItem, DotCMSNavigationItem } from 'dotcms/src/lib/models';
import { from, map } from 'rxjs';

import { dotCmsPageSlugRegex } from '../../configurations/dot-cms-regex';
import { DotCmsFacade } from '../../facades/dot-cms.facade';
import { CustomDotCmsContentlet } from '../../models/contentlet/custom-dotcms-contentlet.model';
import { CustomDotCmsPageMapper } from '../../models/page/custom-dot-cms-page.mapper';
import { CustomDotCmsPageAsset } from '../../models/page/custom-dot-cms-page.model';
import { DotCmsApiService } from '../dot-cms-api/dot-cms-api.service';

export type DotCmsResponseFormat = 'json' | 'render';

export type DotCmsContentDepth = 0 | 1 | 2 | 3;

export type DotCmsNavigationDepth = 1 | 2 | 3 | 4 | 5;

export interface DotCmsApiResponse {
  entity: any;
}

export interface DotCmsSiteRequestParameters {
  hostId?: string;
  language?: number;
}

export interface DotCmsPageRequestParameters extends DotCmsSiteRequestParameters {
  depth?: DotCmsContentDepth;
  personaId?: string;
  format?: DotCmsResponseFormat;
  fireRules?: boolean;
  variantName?: string;
  mode?: 'LIVE' | 'WORKING' | 'EDIT_MODE';
}

export interface DotCmsContentRequestParameters extends DotCmsSiteRequestParameters {
  depth?: DotCmsContentDepth;
  language?: number;
}

@Injectable({ providedIn: 'root' })
export class DotCmsResourceService {
  constructor(private apiService: DotCmsApiService, private dotCMSFacade: DotCmsFacade) {}
  getPageByURI(uri: string, params?: DotCmsPageRequestParameters) {
    let updatedUri = uri;
    if (dotCmsPageSlugRegex.test(uri)) updatedUri = updatedUri.split(dotCmsPageSlugRegex)[1];
    if (uri.startsWith('/')) updatedUri = updatedUri.substring(1);
    const pageProps = {
      path: updatedUri,
      mode: 'LIVE',
      language_id: params.language,
      variantName: params.variantName,
      personaId: params.personaId,
      depth: params.depth,
      siteId: params.hostId,
    };
    return from(this.dotCMSFacade.DOT_CMS_CLIENT.page.get(pageProps)).pipe(
      map(response => CustomDotCmsPageMapper.fromData(response as CustomDotCmsPageAsset))
    );
  }

  getContentByInodeOrIdentifier(inodeOrIdentifier: string, params: DotCmsContentRequestParameters) {
    let httpParams: HttpParams = new HttpParams();
    if (params?.hostId) httpParams = httpParams.set('host_id', params.hostId);
    if (params?.language) httpParams = httpParams.set('language_id', params.language);
    if (params?.depth) httpParams = httpParams.set('depth', params.depth);
    return this.apiService
      .get<DotCmsApiResponse>(`/api/v1/content/${inodeOrIdentifier}`, { params: httpParams })
      .pipe(map(response => response.entity as CustomDotCmsContentlet));
  }

  getNavigation(depth?: DotCmsNavigationDepth, params?: DotCmsSiteRequestParameters) {
    const navProps = {
      path: '/',
      depth,
      language_id: params.language,
      host_id: params.hostId,
    };
    return from(this.dotCMSFacade.DOT_CMS_CLIENT.nav.get(navProps)).pipe(
      map((nav: DotCmsApiResponse) => nav?.entity as DotCMSNavigationItem)
    );
  }

  getConfiguration(params: DotCmsSiteRequestParameters) {
    let httpParams: HttpParams = new HttpParams();
    if (params?.hostId) httpParams = httpParams.set('host_id', params.hostId);
    return this.apiService
      .get<DotCmsApiResponse>(`/api/v1/configuration`, { params: httpParams })
      .pipe(map(response => response.entity as DotCMSConfigurationItem));
  }
}
